import {GROUND_BACKGROUNDS} from '../groundBackgrounds';

/**
 * ПЕПЕЛ С ОСТЫВАЮЩИМИ УГОЛЬКАМИ — эффект пепельной подложки (ash_1).
 *
 * Над выжженной равниной все время идет пепел: хлопья медленно несет ветром
 * матча (тем же, что и облака) и понемногу опускает вниз по экрану. Хлопья
 * кувыркаются — поворачиваются ребром и снова плашмя, — этим пепел и отличается
 * от снега. Малая часть хлопьев — тлеющие угольки: рождаются оранжевыми, со
 * светящимся ореолом, и за полжизни остывают в обычный темный пепел (см.
 * utils/effects/biome/ashfall).
 *
 * ПЕПЕЛ МЕЛКИЙ И ЧАСТЫЙ, ПОЧТИ ВЕСЬ — СЕРЫЙ И ЧЕРНЫЙ: так просил владелец игры,
 * посмотрев мелкий пепел над вулканом. Раньше хлопья были вдвое крупнее и вдвое
 * реже и читались горстью крупных пятен, а не пеплом.
 *
 * Середина картинки — ровная серая дымка без единой приметы, и серый хлопок
 * на ней не виден совсем. Поэтому пепел двух тонов: светлый виден на черных
 * холмах вокруг поля, угольно-темный — на светло-серых пустых ячейках. Угольков
 * — восьмая часть: оранжевое на сером видно всегда, но равнина — пепелище, а не
 * костер (вдоволь угольков — над вулканом, см. ./craterPlume).
 *
 * Рождаются хлопья там, куда смотрит камера, в пределах поля с запасом:
 * рассыпать их по всему пейзажу значило бы оставить в кадре единицы.
 *
 * РАЗМЕР ХЛОПКА И ОРЕОЛА УГОЛЬКА ОТСЮДА БЕРЕТ ПЕПЕЛ ВУЛКАНА (./craterPlume
 * наследует числа flake и cinder.glowScale как есть): меняешь их здесь —
 * посмотри и на вулканическую подложку.
 */
export const ASHFALL_KIND = 'ashfall';

export const ASHFALL_EFFECT = Object.freeze({
  kind: ASHFALL_KIND,
  backgrounds: Object.freeze({
    [GROUND_BACKGROUNDS.ASH_1]: Object.freeze({
      // Пепел идет непрерывно: горсть хлопьев рождается раз в секунду (пауза —
      // от рождения предыдущей), каждая живет свой срок
      firstDelayMs: {min: 200, max: 800},
      intervalMs: {min: 800, max: 1200},
      // Горсть живет до 15 секунд — разом их до полутора десятков
      maxGroups: 20,
      // Партия не начинается с чистого неба: столько горстей рождаются сразу,
      // уже прожившими часть срока
      prewarm: 11,

      // Сколько хлопьев держится в воздухе на одну ячейку области рождения
      // (вдвое больше, чем при прежних крупных хлопьях: было 0.28)…
      densityPerCell: 0.56,
      // …но в среднем не больше стольких (было 54): при отдаленной камере пепел
      // редеет, а спрайтов больше не становится. Это среднее, а не жесткий
      // потолок: горсти рождаются случайно, и на миг хлопьев бывает раза в
      // полтора больше. Спрайтов занято еще больше, чем хлопьев в воздухе: у
      // уголька их два, и горсть держит свои до конца срока — разом до 171, а
      // заведено за партию до 182 (замер и бюджет — в тесте построителя). Гуще
      // некуда: бюджет эффекта — 190 спрайтов
      maxFlakes: 108,
      // Область рождения: поле с таким запасом вокруг (размеров ячейки; четыре
      // ячейки в ширину) и кадр камеры — с таким
      fieldMarginFactor: 8,
      viewMarginFactor: 2,

      flake: {
        // Срок жизни хлопьев горсти. У каждого хлопка он короче на свою долю, до
        // lifeSpread: спрайты горсти заняты, пока жив ее последний хлопок, и
        // большой разнобой сроков держал бы их без дела
        lifeMs: {min: 9000, max: 14000},
        lifeSpread: 0.1,
        // Проступает и тает за столько
        fadeMs: 1200,
        // Хлопья одной горсти появляются не разом, а в течение этого времени
        staggerMs: 1000,
        // Кадр хлопка, размеров ячейки; сам хлопок — около двух третей кадра
        // (от двадцатой до двенадцатой части ширины ячейки). Вдвое мельче
        // прежнего (было 0.26–0.48): крупные хлопья читались пятнами, а темные
        // — мухами. Мельче не видно: на телефоне хлопок — уже две-три точки
        // экрана
        sizeFactor: {min: 0.13, max: 0.24},
        // Снос по ветру и падение вниз по экрану, размеров ячейки в секунду
        windSpeedFactor: {min: 0.7, max: 1.2},
        fallSpeedFactor: {min: 0.2, max: 0.36},
        // Покачивание поперек ветра, размеров ячейки
        swayFactor: {min: 0.4, max: 0.7},
        swayPeriodMs: {min: 3000, max: 5000},
        spinDegPerSecond: {min: 20, max: 60},
        // Кувырок: хлопок становится ребром дважды за этот срок; ребром он
        // сужается до этой доли ширины
        tumblePeriodMs: {min: 1200, max: 2200},
        tumbleEdgeShare: 0.35,
      },

      // ДОЛИ ВИДОВ ХЛОПЬЕВ: серого и черного пепла — без малого девять десятых,
      // угольков — восьмая часть.
      // Светлый пепел: виден на обугленных холмах вокруг поля
      pale: {share: 0.48, tint: 0xe4e0d6, alpha: {min: 0.6, max: 0.85}},
      // Угольно-темный: виден на светло-серых пустых ячейках. Его почти столько
      // же, сколько светлого: мелкие темные хлопья читаются сажей (крупных
      // нельзя было больше четверти — поле казалось засиженным мухами). Но не
      // больше, чем светлого: черное на светлых ячейках заметнее
      charcoal: {share: 0.4, tint: 0x2b2927, alpha: {min: 0.55, max: 0.75}},
      // Тлеющий уголек: хлопок остывает от оранжевого до темного, над ним —
      // светящийся ореол, пока уголек горяч. Угольков — явное меньшинство
      // (в воздухе разом около тринадцати, из них светится половина)
      cinder: {
        share: 0.12,
        hotTint: 0xff7a28,
        coolTint: 0x4a3a34,
        hotAlpha: 0.95,
        coolAlpha: 0.7,
        // За какую долю жизни уголек остывает; дальше он — обычный темный пепел
        coolShare: {min: 0.55, max: 0.65},
        glowTint: 0xff6a24,
        glowAlpha: 0.9,
        // Кадр ореола крупнее кадра хлопка во столько раз (у мягкого пятна
        // яркая середина — около трети кадра): ореол мельчает вместе с хлопком
        glowScale: 3,
        // Ореол мерцает
        flickerHz: {min: 6, max: 9},
        flickerDepth: 0.2,
      },
    }),
  }),
});
